import { action, noop } from '@reatom/framework'
import i18next from 'i18next'

import { pipe, Result } from '@blade/std-lib'
import { userAtom } from '@root/entities/auth'
import { refreshProfileAction } from '@root/entities/profile'
import { refreshUserAction } from '@root/features/initialization/atoms/common/reatom-me'
import { dependenciesAtom } from '@root/features/shared/dependencies'
import { DomUtil } from '@root/shared'
import { ApiError, apiRequest } from '@root/shared/api'
import { FastifyErrorCode } from '@root/shared/api/api-error-code'
import { reatomRequest } from '@root/shared/lib'
import { showErrorSnackbarAction } from '@root/shared/ui'
import { showSnackbarAction } from '@root/shared/ui-kit'

import type { UnknownError } from '@root/shared/core/Error/Error'

const isFileTooLargeError = (error: UnknownError | ApiError.ApiError) => {
  return ApiError.isOfCode(FastifyErrorCode.ContentBodyTooLarge)(error)
}

type ChangeProfileInfoPayload = {
  readonly firstName?: undefined | string
  readonly lastName?: undefined | string
}

const changeProfileInfoRequestAction = action(
  async (ctx, payload: ChangeProfileInfoPayload) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)

    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.user.userProfilePartialUpdate, [
        {
          firstName: payload.firstName,
          lastName: payload.lastName,
        },
      ]),
    )

    return pipe(result, Result.mapError(ApiError.fallbackToUnknown))
  },
  'changeProfileInfoRequestAction',
)

type ChangeProfileAvatarPayload = {
  readonly avatar: File | null
}

const changeProfileAvatarRequestAction = action(
  async (ctx, payload: ChangeProfileAvatarPayload) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)
    const avatar = payload.avatar

    const avatarDtoResult = avatar
      ? await ctx.schedule(() => DomUtil.blobToBase64(avatar))
      : // eslint-disable-next-line unicorn/no-null
        Result.succeed(null)

    if (Result.isFailure(avatarDtoResult)) {
      return Result.fail(avatarDtoResult.failure)
    }

    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.user.userProfileChangeAvatarCreate, [
        {
          avatar: avatarDtoResult.success,
        },
      ]),
    )

    return pipe(
      result,
      Result.map((x) => x.avatarUrl),
    )
  },
  'changeProfileAvatarRequestAction',
)

type ChangeProfileFormPayload = {
  readonly avatar?: File | undefined | null
  readonly firstName?: undefined | string
  readonly lastName?: undefined | string
}

const changeProfileAction = action(
  async (ctx, payload: ChangeProfileFormPayload) => {
    const { avatar } = payload

    const user = ctx.get(userAtom)
    const shouldUpdateAvatar = avatar || (avatar === null && user?.avatarSrc)

    if (shouldUpdateAvatar) {
      const avatarChangeResult = await ctx.schedule(() =>
        changeProfileAvatarRequestAction(ctx, { avatar }),
      )

      if (Result.isFailure(avatarChangeResult)) {
        if (isFileTooLargeError(avatarChangeResult.failure)) {
          showSnackbarAction(ctx, {
            text: i18next.t('common.file-size-too-big'),
            intent: 'error',
          })
          return Result.fail(avatarChangeResult.failure)
        }
        showErrorSnackbarAction(ctx, avatarChangeResult.failure)
        return Result.fail(avatarChangeResult.failure)
      }

      showSnackbarAction(ctx, {
        text: i18next.t('profile.profile_avatar_updated'),
        intent: 'success',
      })
    }

    const profileChangeResult = await ctx.schedule(() =>
      changeProfileInfoRequestAction(ctx, {
        firstName: payload.firstName,
        lastName: payload.lastName,
      }),
    )

    if (Result.isFailure(profileChangeResult)) {
      return Result.fail(profileChangeResult.failure)
    }

    showSnackbarAction(ctx, {
      text: i18next.t('profile.profile_info_updated'),
      intent: 'success',
    })

    return profileChangeResult
  },
  'changeProfileAction',
)

export const reatomEditProfileForm = () => {
  // eslint-disable-next-line @reatom/unit-naming-rule
  const requestState = reatomRequest({
    execAction: changeProfileAction,
    async onResult(ctx, result) {
      if (Result.isSuccess(result)) {
        refreshProfileAction(ctx)
        refreshUserAction(ctx)
      }
    },
  })

  return { requestState }
}

export const openEditProfileFormModalEvent = action(
  noop,
  'openEditProfileFormModalEvent',
)
